/**
 * x-results-list directive.
 *
 * Manages infinite scroll and bi-directional page loading for a paginated
 * product/result list. When the component enters the viewport, it prefetches
 * the adjacent pages (previous and next). Previous pages are loaded
 * automatically via an IntersectionObserver on a sentinel element; next pages
 * can be loaded by calling `renderNextPage()` directly.
 *
 * Scroll position is preserved when prepending previous-page items so the
 * user's view does not jump.
 *
 * @options
 *   sectionId                  - The Shopify section ID for the section render URL.
 *   resourceUrl                - The pathname of the collection or search page.
 *   listSelector               - CSS selector for the results grid container element.
 *   listItemSelector           - CSS selector for each individual result item.
 *   paginationSelector         - CSS selector for the pagination element to morph
 *                                after loading a next page.
 *   viewMorePreviousSelector   - CSS selector for the sentinel element that triggers
 *                                previous-page loading on intersection
 *                                (default: ".view-more-previous-marker").
 *
 * @events dispatched
 *   results-previous-page-updated - Fired after a previous page is prepended.
 *   results-next-page-updated     - Fired after a next page is appended.
 *
 * @example
 *   <div x-results-list="{ sectionId: 'main-collection', resourceUrl: '/collections/all', listSelector: '.product-grid', listItemSelector: '.product-card', paginationSelector: '.pagination' }">
 *   </div>
 */
(() => {
  const root = (
    Alpine,
    el,
    {
      sectionId,
      resourceUrl,
      listSelector,
      listItemSelector,
      paginationSelector,
      viewMorePreviousSelector = ".view-more-previous-marker",
    },
  ) => {
    Alpine.bind(el, () => ({
      /** Prefetches the previous and next page when the component enters the viewport. */
      ["x-intersect:enter.once.margin.400px"]() {
        this.fetchPrev();
        this.fetchNext();
      },

      ["x-data"]() {
        return {
          infinityScrollObserver: null,

          /**
           * Observes the view-more-previous sentinel element on mount.
           */
          init() {
            this.observeViewMorePrevious();
          },

          /**
           * Disconnects the IntersectionObserver on teardown.
           */
          destroy() {
            if (this.infinityScrollObserver) {
              this.infinityScrollObserver.disconnect();
            }
          },

          /**
           * Creates an IntersectionObserver on the view-more-previous sentinel
           * element. When the sentinel intersects the viewport, the previous
           * page is rendered and prepended to the grid.
           */
          observeViewMorePrevious() {
            const viewMorePrevious = this.$root.querySelector(
              viewMorePreviousSelector,
            );

            // Return early if the sentinel element does not exist.
            if (!viewMorePrevious) return;

            // Create the observer if it does not already exist.
            if (!this.infinityScrollObserver) {
              this.infinityScrollObserver = new IntersectionObserver(
                async (entries) => {
                  for (const entry of entries) {
                    if (entry.isIntersecting) {
                      const viewMorePrevious = this.$root.querySelector(
                        viewMorePreviousSelector,
                      );

                      if (entry.target === viewMorePrevious) {
                        this.renderPreviousPage();
                      }
                    }
                  }
                },
                {
                  rootMargin: "100px",
                },
              );
            }

            this.infinityScrollObserver.observe(viewMorePrevious);
          },

          /**
           * Prefetches an adjacent page (previous or next) if it is within bounds.
           * @param {"previous"|"next"} page - Which adjacent page to prefetch.
           */
          fetchAdjacent(page) {
            const pageInfo = this.resolvePageInfo(page);

            if (!pageInfo || !this.shouldFetchPage(pageInfo)) {
              return;
            }

            this.prefetchPage(pageInfo);
          },

          /**
           * Returns true when the given page info represents a valid, in-range page.
           * @param {{ page: number, url: URL }|null} pageInfo - The page info to check.
           * @returns {boolean}
           */
          shouldFetchPage(pageInfo) {
            if (!pageInfo) return false;

            const grid = this.$root.querySelector(`${listSelector}`);
            const lastPage = grid?.dataset.lastPage;

            if (
              !lastPage ||
              pageInfo.page < 1 ||
              pageInfo.page > Number(lastPage)
            ) {
              return false;
            }

            return true;
          },

          /**
           * Initiates a prefetch for the given page URL via the prefetch manager.
           * @param {{ page: number, url: URL }} pageInfo - The page to prefetch.
           */
          prefetchPage(pageInfo) {
            if (!this.shouldFetchPage(pageInfo)) return;
            this.$app.$prefetchManager.prefetch(pageInfo.url.toString());
          },

          /**
           * Prefetches the next page.
           */
          fetchNext() {
            this.fetchAdjacent("next");
          },

          /**
           * Prefetches the previous page.
           */
          fetchPrev() {
            this.fetchAdjacent("previous");
          },

          /**
           * Resolves the page number and URL for the page adjacent to the first
           * or last item currently in the grid.
           * @param {"previous"|"next"} type - Which adjacent page to resolve.
           * @returns {{ page: number, url: URL }|undefined} The page info, or
           *   undefined when the grid is empty.
           */
          resolvePageInfo(type) {
            const items = this.gridItemsList;

            const isPrevious = type === "previous";

            if (!items.length) return;

            const targetItem = items[isPrevious ? 0 : items.length - 1];

            if (!targetItem) return;

            const currentItemPage = Number(targetItem.dataset.page);
            const page = isPrevious ? currentItemPage - 1 : currentItemPage + 1;

            const url = new URL(window.location.href);

            url.pathname = resourceUrl;
            url.searchParams.set("page", page.toString());
            url.searchParams.set("section_id", sectionId);
            url.hash = "";

            return {
              page,
              url,
            };
          },

          /**
           * Fetches and prepends the previous page items to the grid. Adjusts
           * the scroll position to prevent layout jump after insertion.
           * Dispatches `results-previous-page-updated` on completion.
           * @returns {Promise<void>}
           */
          async renderPreviousPage() {
            const grid = this.gridEl;

            if (!grid) return;

            const previousPage = this.resolvePageInfo("previous");
            if (!previousPage || !this.shouldFetchPage(previousPage)) return;

            const responseText = await this.$app.$prefetchManager
              .fetch(previousPage.url.toString())
              .then((response) => response.text());

            const html = this.$app.$parseHtml(responseText);

            const previousPageItemElements = this.getGridForPage(html);

            // Store the current scroll position and the top offset of the first element.
            const scrollTop = window.scrollY;
            const firstElement = grid.firstElementChild;
            const oldHeight = firstElement
              ? firstElement.getBoundingClientRect().top + window.scrollY
              : 0;

            // Prepend the new elements.
            grid.prepend(...previousPageItemElements);

            // Remove the section_id before the URL goes into the history.
            previousPage.url.searchParams.delete("section_id");

            // Adjust scroll position to maintain the same view after prepending.
            if (firstElement) {
              const newHeight =
                firstElement.getBoundingClientRect().top + window.scrollY;

              const heightDiff = newHeight - oldHeight;

              window.scrollTo({
                top: scrollTop + heightDiff,
                behavior: "instant",
              });
            }

            this.$dispatch("results-previous-page-updated", {
              ...previousPage,
            });

            requestIdleCallback(() => {
              this.fetchPrev();
            });
          },

          /**
           * Fetches and appends the next page items to the grid. Updates the
           * browser URL and morphs the pagination element if `paginationSelector`
           * is set. Dispatches `results-next-page-updated` on completion.
           * @returns {Promise<void>}
           */
          async renderNextPage() {
            const grid = this.gridEl;

            if (!grid) return;

            const nextPage = this.resolvePageInfo("next");
            if (!nextPage || !this.shouldFetchPage(nextPage)) return;

            const responseText = await this.$app.$prefetchManager
              .fetch(nextPage.url.toString())
              .then((response) => response.text());

            const html = this.$app.$parseHtml(responseText);

            const nextPageItemElements = this.getGridForPage(html);

            if (!nextPageItemElements) return;

            // Append the new elements.
            grid.append(...nextPageItemElements);

            // Move keyboard focus to the first focusable element in the first new item.
            const firstFocusable = nextPageItemElements[0]?.querySelector(
              'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])',
            );
            firstFocusable?.focus();

            // Remove the section_id before pushing to history.
            nextPage.url.searchParams.delete("section_id");

            history.pushState("", "", nextPage.url.toString());

            // Replace the pagination element.
            if (paginationSelector) {
              this.$morph(this.$root, html, {
                morphSelectors: [paginationSelector],
              });
            }

            this.$dispatch("results-next-page-updated", { ...nextPage });

            requestIdleCallback(() => {
              this.fetchNext();
            });
          },

          /**
           * Returns the direct child items from the list element in the given
           * parsed HTML document.
           * @param {Document} html - The parsed HTML document.
           * @returns {NodeListOf<Element>|undefined} The item elements, or
           *   undefined when the list element is not found.
           */
          getGridForPage(html) {
            if (!html) return;

            const gridElement = html.querySelector(`${listSelector}`);

            if (!gridElement) return;

            return gridElement.querySelectorAll(`:scope > ${listItemSelector}`);
          },

          /**
           * Returns an array of all current list item elements in the grid.
           * @returns {Element[]}
           */
          get gridItemsList() {
            return Array.from(
              this.$root.querySelectorAll(`${listItemSelector}`),
            );
          },

          /**
           * Returns the grid container element.
           * @returns {Element|null}
           */
          get gridEl() {
            return this.$root.querySelector(`${listSelector}`);
          },
        };
      },
    }));
  };

  document.addEventListener("alpine:init", () => {
    Alpine.directive("results-list", (el, { expression }, { evaluate }) => {
      root(Alpine, el, expression ? evaluate(expression) : {});
    });
  });
})();
